import { PhraseProgressBar } from "@/shared/components/PhraseProgressBar";
import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { type AISummary, AISummaryStatus } from "@/shared/domain/ai_summary";
import { Banner, RobotoButton, Spinner } from "@/shared/foundation/primitives";

import { AISummaryMarkdownLink, EntityLink } from "../AISummaryMarkdownLink";
import { AISummaryMetadataBar } from "../AISummaryMetadataBar";

import styles from "./AISummaryPanel.module.css";

/** The record a summary describes. */
export interface AISummarySubject {
  /** Kind of record. Names the record in the empty state and in feedback events. */
  type: "dataset" | "session";
  /** The record's id. A link in the summary back to this record renders as plain text. */
  id: string;
}

export interface AISummaryPanelProps {
  /**
   * The stored summary; `null` when none has been generated, `undefined`
   * before the first read settles.
   */
  summary: AISummary | null | undefined;
  /** The first read is in flight. */
  isLoading: boolean;
  /** The read failed for a reason other than "no summary yet". */
  isLoadError: boolean;
  /** A generate request is in flight. */
  isGenerating: boolean;
  /** The last generate request was refused or failed. */
  isGenerateError: boolean;
  /** Starts generating a summary, whether or not one exists. */
  onGenerate: () => void;
  /**
   * A link the summary makes back to this record renders as plain text, since
   * the reader is already looking at it.
   */
  subject: AISummarySubject;
}

/**
 * The AI summary of one record: the generated text with its age and a
 * regenerate control, a "Generate summary" call to action when there is none,
 * and progress while the agent writes it. Generation only starts on a click;
 * opening the panel never spends credits.
 */
export function AISummaryPanel({
  summary,
  isLoading,
  isLoadError,
  isGenerating,
  isGenerateError,
  onGenerate,
  subject,
}: AISummaryPanelProps) {
  const isPending = summary?.status === AISummaryStatus.Pending;
  const isWriting = isGenerating || (isPending && summary?.text === "");
  // A complete summary with no text reads like no summary at all, so it gets
  // the same call to action.
  const isEmpty =
    summary === null ||
    (summary?.status === AISummaryStatus.Complete && summary.text === "");
  const showGenerate = !isLoading && !isWriting && !isLoadError && isEmpty;
  const showText =
    !isLoading &&
    !isWriting &&
    summary !== null &&
    summary !== undefined &&
    summary.status !== AISummaryStatus.Failed &&
    summary.text !== "";

  const isSubject = (type: string, id: string) =>
    type === subject.type && id === subject.id;

  return (
    <div className={styles.panel}>
      {summary?.created !== undefined && !isLoading && !showGenerate && (
        <AISummaryMetadataBar
          created={summary.created}
          isRegenerating={isGenerating || isPending}
          onRegenerateClick={onGenerate}
          // Feedback and regenerate analytics name the record `datasetId` or
          // `sessionId`.
          context={{
            [`${subject.type}Id`]: subject.id,
            created: summary.created,
          }}
        />
      )}

      {isLoading && (
        <div className={styles.progress}>
          <Spinner size="lg" />
        </div>
      )}

      {!isLoading && isWriting && (
        <div className={styles.progress}>
          <PhraseProgressBar />
        </div>
      )}

      {showGenerate && (
        <div className={styles.empty}>
          <p className={styles.emptyText}>
            No AI summary is available for this {subject.type} yet.
          </p>
          <RobotoButton variant="primary" onClick={onGenerate}>
            Generate summary
          </RobotoButton>
        </div>
      )}

      {!isLoading && isLoadError && (
        <Banner
          variant="error"
          text="Failed to load summary. Please try again later."
        />
      )}

      {isGenerateError && (
        <Banner
          variant="error"
          text="Couldn't generate the summary. Try again."
        />
      )}

      {summary?.status === AISummaryStatus.Failed && (
        <Banner
          variant="error"
          text="Failed to generate summary. Please try regenerating."
        />
      )}

      {showText && (
        <RobotoMarkdown
          content={summary.text}
          datasetLinkRenderer={({ datasetId, text }) =>
            isSubject("dataset", datasetId) ? (
              <span>{text}</span>
            ) : (
              <AISummaryMarkdownLink datasetId={datasetId} text={text} />
            )
          }
          fileLinkRenderer={({ fileId, text, time }) => (
            <AISummaryMarkdownLink fileId={fileId} text={text} time={time} />
          )}
          topicLinkRenderer={({ topicId, text, time }) => (
            <AISummaryMarkdownLink topicId={topicId} text={text} time={time} />
          )}
          messagePathLinkRenderer={({ messagePathId, text, time }) => (
            <AISummaryMarkdownLink
              messagePathId={messagePathId}
              text={text}
              time={time}
            />
          )}
          entityLinkRenderer={(props) =>
            isSubject(props.entityType, props.entityId) ? (
              <span>{props.text}</span>
            ) : (
              <EntityLink {...props} />
            )
          }
        />
      )}
    </div>
  );
}
